import { brand } from "@pervasivemind/bi-components/brand";
import { Badge, Button, Card, CardBody, Tabs, type TabItem } from "@omnitok/ui";
import { AlertCircle, ArrowLeft, Save } from "lucide-react";
import { Spinner } from "@pervasivemind/bi-components";
import {
  useCallback,
  useEffect,
  useState,
  type FormEvent,
} from "react";
import { Link, useNavigate, useParams } from "react-router-dom";

import { useT } from "@/i18n";
import { projectsApi } from "@/lib/builder-api";
import {
  hasProjectName,
  parseProjectName,
  serializeProjectName,
} from "@/lib/project-name";
import type { DisplayLabel } from "@/types";
import { LoaderScheduleFields } from "@/pages/loader/LoaderScheduleFields";
import {
  ArchivosConfigFields,
  GeneralFields,
  MaestrosConfigFields,
  ProjectDataFields,
  SLUG_PATTERN,
} from "@/pages/ProjectsPage";
import type {
  FilesConfig,
  MasterFilesConfig,
  Project,
} from "@/types";

type FormTab = "general" | "maestros" | "archivos" | "data" | "loader";

/**
 * Full-page Project editor — create + edit modes share the same chrome.
 *
 * Routes:
 *   /projects/new            → create mode (no slug param)
 *   /projects/:slug/edit     → edit mode (loads project, all 5 tabs)
 *
 * Iter 27 follow-up: replaces the cramped ``ProjectFormModal`` that
 * lived inside ``ProjectsPage.tsx``. The sub-form components
 * (``GeneralFields`` / ``MaestrosConfigFields`` / ``ArchivosConfigFields``
 * / ``ProjectDataFields`` / ``LoaderScheduleFields``) are reused
 * verbatim — they already took state as props, so the page wrapper
 * just owns the state and the submit handler.
 *
 * Header chrome mirrors ``ReportBuilderPage`` for consistency:
 * back link · title · save button on the right. Tabs sit under the
 * header. The Data and Loader tabs only show in edit mode (matches
 * the previous modal behaviour — they need a saved project to act
 * on).
 */
export function ProjectEditPage(): JSX.Element {
  const { slug: routeSlug } = useParams<{ slug?: string }>();
  const navigate = useNavigate();
  const { t } = useT();
  const isEdit = Boolean(routeSlug);

  // ── Initial project (edit mode) ───────────────────────────
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState<boolean>(isEdit);
  const [loadError, setLoadError] = useState<string | null>(null);

  const loadProject = useCallback(async (): Promise<void> => {
    if (!routeSlug) return;
    setLoading(true);
    setLoadError(null);
    try {
      const fetched = await projectsApi.get(routeSlug);
      setProject(fetched);
    } catch (err) {
      setLoadError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setLoading(false);
    }
  }, [routeSlug, t]);

  useEffect(() => {
    if (isEdit) void loadProject();
  }, [isEdit, loadProject]);

  // ── Form state ────────────────────────────────────────────
  const [slug, setSlug] = useState("");
  const [name, setName] = useState<DisplayLabel>("");
  const [description, setDescription] = useState("");
  const [color, setColor] = useState(brand.primary.DEFAULT);
  const [icon, setIcon] = useState("");
  const [dashboardEnabled, setDashboardEnabled] = useState(true);
  const [maestrosEnabled, setMaestrosEnabled] = useState(true);
  const [filesEnabled, setFilesEnabled] = useState(true);
  const [masterFilesConfig, setMasterFilesConfig] =
    useState<MasterFilesConfig>({});
  const [filesConfig, setFilesConfig] = useState<FilesConfig>({});

  // Seed form state once the project loads (edit mode).
  useEffect(() => {
    if (!project) return;
    setSlug(project.slug);
    setName(parseProjectName(project.name));
    setDescription(project.description ?? "");
    setColor(project.color ?? brand.primary.DEFAULT);
    setIcon(project.icon ?? "");
    setDashboardEnabled(project.dashboard_enabled ?? true);
    setMaestrosEnabled(project.maestros_enabled ?? true);
    setFilesEnabled(project.files_enabled ?? true);
    setMasterFilesConfig(project.master_files_config ?? {});
    setFilesConfig(project.files_config ?? {});
  }, [project]);

  const [activeTab, setActiveTab] = useState<FormTab>("general");
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const trimmedSlug = slug.trim();
  const slugValid = SLUG_PATTERN.test(trimmedSlug) && trimmedSlug.length >= 2;
  const hasAnySection = dashboardEnabled || maestrosEnabled || filesEnabled;
  const canSubmit =
    slugValid && hasProjectName(name) && hasAnySection && !submitting;

  const handleSubmit = async (
    event: FormEvent<HTMLFormElement>,
  ): Promise<void> => {
    event.preventDefault();
    if (!canSubmit) return;
    setSubmitError(null);
    setSubmitting(true);
    try {
      if (isEdit && project) {
        await projectsApi.update(project.slug, {
          slug: trimmedSlug,
          name: serializeProjectName(name),
          description: description.trim(),
          color: color.trim() || undefined,
          icon,
          dashboard_enabled: dashboardEnabled,
          maestros_enabled: maestrosEnabled,
          files_enabled: filesEnabled,
          master_files_config: masterFilesConfig,
          files_config: filesConfig,
        });
      } else {
        await projectsApi.create({
          slug: trimmedSlug,
          name: serializeProjectName(name),
          description: description.trim() || undefined,
          color: color.trim() || undefined,
          icon: icon || undefined,
          dashboard_enabled: dashboardEnabled,
          maestros_enabled: maestrosEnabled,
          files_enabled: filesEnabled,
          master_files_config: masterFilesConfig,
          files_config: filesConfig,
        });
      }
      // PR-0 builder reorg: after CREATE, land inside the new
      // project's hub (the natural next step is adding a report or
      // its data source). Edit mode is only reachable via legacy
      // URLs — send it to the hub too.
      navigate(`/projects/${encodeURIComponent(trimmedSlug)}`);
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : t("common.error"));
    } finally {
      setSubmitting(false);
    }
  };

  // Data + Loader tabs only show in edit mode — they need a saved
  // project to act on (same restriction the modal had).
  const tabs: TabItem[] = [
    { id: "general", label: t("projects.form.tab.general") },
    { id: "maestros", label: t("projects.form.tab.maestros") },
    { id: "archivos", label: t("projects.form.tab.archivos") },
    ...(isEdit
      ? [{ id: "data", label: t("projects.form.tab.data") } as TabItem]
      : []),
    ...(isEdit
      ? [{ id: "loader", label: t("projects.form.tab.loader") } as TabItem]
      : []),
  ];

  // ── Render ────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="flex h-[70vh] items-center justify-center text-sm text-neutral-500">
        <Spinner size={16} className="mr-2" ariaLabel={t("common.loading")} />
        {t("common.loading")}
      </div>
    );
  }

  if (isEdit && loadError) {
    return (
      <div className="mx-auto mt-12 max-w-md px-6">
        <Card variant="bordered" className="border-error/40 bg-error/5">
          <CardBody>
            <div className="flex items-start gap-3 text-sm text-error-dark">
              <AlertCircle size={16} className="mt-0.5 shrink-0" />
              <div className="flex-1">
                <p className="font-medium">{loadError}</p>
                <Button
                  variant="danger"
                  ghost
                  size="xs"
                  onClick={() => void loadProject()}
                  className="mt-3"
                >
                  {t("common.retry")}
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>
        <Link
          to="/"
          className="mt-4 inline-flex items-center gap-1 text-sm text-neutral-600 hover:text-neutral-900"
        >
          <ArrowLeft size={14} /> {t("nav.projects")}
        </Link>
      </div>
    );
  }

  const pageTitle = isEdit
    ? t("projects.edit.title")
    : t("projects.new");

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title={pageTitle}
        slug={isEdit ? project?.slug : null}
        canSubmit={canSubmit}
        submitting={submitting}
        isEdit={isEdit}
      />

      <div className="border-b border-neutral-200 bg-white px-4 sm:px-6">
        <Tabs
          items={tabs}
          activeId={activeTab}
          onChange={(id: string) => setActiveTab(id as FormTab)}
          variant="underline"
          size="sm"
        />
      </div>

      <div className="flex-1 overflow-y-auto bg-neutral-50">
        <div className="mx-auto w-full max-w-4xl px-4 py-8 sm:px-6">
          <form
            id="project-form"
            onSubmit={handleSubmit}
            className="space-y-4"
            noValidate
          >
            {activeTab === "general" && (
              <Card variant="bordered">
                <CardBody className="space-y-4">
                  <GeneralFields
                    slug={slug}
                    slugValid={slugValid}
                    onSlug={setSlug}
                    name={name}
                    onName={setName}
                    description={description}
                    onDescription={setDescription}
                    color={color}
                    onColor={setColor}
                    icon={icon}
                    onIcon={setIcon}
                    dashboardEnabled={dashboardEnabled}
                    onDashboardEnabled={setDashboardEnabled}
                    maestrosEnabled={maestrosEnabled}
                    onMaestrosEnabled={setMaestrosEnabled}
                    filesEnabled={filesEnabled}
                    onFilesEnabled={setFilesEnabled}
                    hasAnySection={hasAnySection}
                    submitting={submitting}
                    isEdit={isEdit}
                  />
                </CardBody>
              </Card>
            )}

            {activeTab === "maestros" && (
              <Card variant="bordered">
                <CardBody className="space-y-3">
                  <MaestrosConfigFields
                    config={masterFilesConfig}
                    onChange={setMasterFilesConfig}
                    disabled={submitting}
                  />
                </CardBody>
              </Card>
            )}

            {activeTab === "archivos" && (
              <Card variant="bordered">
                <CardBody className="space-y-3">
                  <ArchivosConfigFields
                    config={filesConfig}
                    onChange={setFilesConfig}
                    disabled={submitting}
                  />
                </CardBody>
              </Card>
            )}

            {activeTab === "data" && project && (
              <Card variant="bordered">
                <CardBody>
                  <ProjectDataFields project={project} onSaved={setProject} />
                </CardBody>
              </Card>
            )}

            {activeTab === "loader" && project && (
              <Card variant="bordered">
                <CardBody>
                  <LoaderScheduleFields project={project} />
                </CardBody>
              </Card>
            )}

            {submitError && (
              <div
                role="alert"
                className="rounded-md border border-error/40 bg-error/5 px-3 py-2 text-sm text-error-dark"
              >
                {submitError}
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
  );
}

interface PageHeaderProps {
  title: string;
  slug: string | null | undefined;
  canSubmit: boolean;
  submitting: boolean;
  isEdit: boolean;
}

function PageHeader({
  title,
  slug,
  canSubmit,
  submitting,
  isEdit,
}: PageHeaderProps): JSX.Element {
  const { t } = useT();
  return (
    <div className="flex items-center justify-between gap-3 border-b border-neutral-200 bg-white px-4 py-3 sm:px-6">
      <div className="min-w-0 flex-1">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900"
        >
          <ArrowLeft size={12} /> {t("nav.projects")}
        </Link>
        <div className="mt-0.5 flex min-w-0 items-center gap-2">
          <h1 className="min-w-0 truncate font-display text-lg font-extrabold leading-tight tracking-[-0.3px] text-neutral-800">
            {title}
          </h1>
          {isEdit && (
            <Badge variant="neutral" size="sm" pill className="shrink-0">
              {t("common.edit")}
            </Badge>
          )}
        </div>
        {slug && (
          <p className="font-mono text-xs text-neutral-500">{slug}</p>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        <Link to="/" className="inline-flex">
          <Button ghost size="sm" disabled={submitting}>
            {t("common.cancel")}
          </Button>
        </Link>
        <Button
          type="submit"
          form="project-form"
          variant="primary"
          size="sm"
          disabled={!canSubmit}
          loading={submitting}
          leftIcon={<Save size={14} />}
        >
          {isEdit ? t("common.save") : t("common.create")}
        </Button>
      </div>
    </div>
  );
}
